import { gql, useQuery } from '@apollo/client';
import IconArrowRight from '@apollo/icons/default/IconArrowRight.svg';
import IconCloud from '@apollo/icons/default/IconCloud.svg';
import IconSupport from '@apollo/icons/default/IconSupport.svg';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Divider,
  IconButton,
  Link,
  Spinner,
  StatusBadge,
  StatusBadgeProps,
} from '@apollo/orbit';
import { capitalize } from 'lodash';
import React, { ReactElement, useMemo } from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { useShowToast } from 'src/components/toast/Toast';
import { CloudOnboardingFragment } from 'src/hooks/useCloudOnboarding';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { getRouterIsInitiating } from 'src/hooks/useIsCloudEndpointInitiating';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { useUpsertRouterConfig } from 'src/hooks/useUpsertRouterConfig';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { graphRefToString, useGraphRef } from '../hooks/useGraphRef';
import { launchesRouteConfig } from '../routes';

import {
  RouterConfigEditor,
  RouterConfigEditorFragment,
} from './variantSettingsCards/routerConfigEditor/RouterConfigEditor';
import { SecretsList } from './variantSettingsCards/secretsList/SecretsList';

const VariantRoutingSettingsFragment = gql`
  fragment VariantRoutingSettingsFragment on Service {
    id
    graphType
    variant(name: $graphVariant) {
      id
      url
      name
      router {
        id
        status
        routerVersion {
          core
          configSchema
        }
      }
      latestLaunch {
        id
        createdAt
        status
      }
      permissions {
        canManageBuildConfig
        canQueryBuildConfig
      }
      ...RouterConfigEditorFragment
    }
  }
  ${RouterConfigEditorFragment}
`;

const VariantRoutingSettingsPageQuery = gql`
  query VariantRoutingSettingsPageQuery(
    $serviceId: ID!
    $graphVariant: String!
    $accountId: ID!
    $graphRef: String!
  ) {
    service(id: $serviceId) {
      id
      ...VariantRoutingSettingsFragment
    }
    account(id: $accountId) {
      id
      ...CloudOnboardingFragment
    }
  }
  ${VariantRoutingSettingsFragment}
  ${CloudOnboardingFragment}
`;

export const VariantRoutingSettingsPage = () => {
  const [orgId] = useCurrentAccountId();
  const graphRef = useGraphRef();
  const graphRefString = graphRefToString(graphRef);
  const trackCustomEvent = useTrackCustomEvent();
  const { showToasts } = useShowToast();
  const { upsertRouterConfig, upsertRouterError, upsertResponseData } =
    useUpsertRouterConfig();

  const { data, loading } = useQuery<
    GraphQLTypes.VariantRoutingSettingsPageQuery,
    GraphQLTypes.VariantRoutingSettingsPageQueryVariables
  >(VariantRoutingSettingsPageQuery, {
    variables: {
      serviceId: graphRef?.graphId as string,
      graphVariant: graphRef?.graphVariant as string,
      accountId: orgId as string,
      graphRef: graphRefString as string,
    },
    skip: !graphRef || !orgId,
  });

  const handleSaveRouterConfig = React.useCallback(
    async (newRouterConfig: string) => {
      if (!graphRef) return;
      try {
        const upsertRouterConfigResult = await upsertRouterConfig({
          graphId: graphRef.graphId,
          variant: graphRef.graphVariant,
          newRouterConfig,
        });
        if (
          upsertRouterConfigResult.data?.service?.variant?.upsertRouterConfig
            ?.__typename !== 'RouterUpsertFailure' &&
          !upsertRouterConfigResult.errors
        ) {
          // successfully updated
          trackCustomEvent({
            action: 'saved_router_config',
            category: 'Cloud Router Settings',
            label: `graphRef:${graphRefToString(graphRef)}`,
          });
          showToasts({
            heading: (
              <span className="text-base font-normal text-primary">
                Your router configuration changes have been saved.
              </span>
            ),
            level: 'success',
            stableId: 'save-router-config-success',
            message: false,
            dismissable: true,
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
    [graphRef, showToasts, trackCustomEvent, upsertRouterConfig],
  );

  const endpoint = data?.service?.variant?.url ?? '';
  const location = useLocation();

  const routerIsInitiating = useMemo(
    () =>
      // default to true until we get data back, we don't render anything
      // based on this until data comes back anyways
      !data?.service?.variant ||
      getRouterIsInitiating({
        graphType: data.service.graphType,
        latestLaunch: data.service.variant?.latestLaunch,
        routerStatus: data.service.variant?.router?.status,
      }),
    [data?.service?.graphType, data?.service?.variant],
  );

  const allSubgraphs = useMemo(
    () => data?.service?.variant?.subgraphs?.map(({ name }) => name) ?? [],
    [data?.service?.variant?.subgraphs],
  );

  if (loading) {
    return (
      <div className="grid h-full place-items-center">
        <Loading size="lg" />
      </div>
    );
  }

  if (!data?.service) {
    return <NotFound />;
  }

  const routerStatus = data.service.variant?.router?.status;
  const coreVersion = data.service.variant?.router?.routerVersion?.core;
  const routerVersion = coreVersion ? `v${coreVersion}` : '';

  const routerConfigSchema =
    data.service.variant?.router?.routerVersion?.configSchema;

  return (
    <>
      <div className="flex flex-col gap-4 lg:flex-row">
        <Card className="flex-1">
          <CardHeader>
            <CardTitle>API Endpoint</CardTitle>
          </CardHeader>
          <Divider />
          <CardBody>
            <div className="flex items-center">
              <IconCloud className="mr-2 min-h-4 min-w-4 text-secondary" />
              <Link isExternal className="truncate" to={endpoint}>
                {endpoint}
              </Link>
            </div>
            <p className="mt-2 text-base text-secondary">
              This is the endpoint where{' '}
              <span className="font-semibold text-primary">
                {graphRefToString(graphRef)}
              </span>
              's router is hosted. Use this endpoint to query your supergraph.
            </p>
          </CardBody>
        </Card>
        <Card className="flex-1">
          <CardHeader>
            <CardTitle>Router Status</CardTitle>
            {(routerStatus === GraphQLTypes.RouterStatus.DELETED ||
              (routerStatus === undefined && !routerIsInitiating)) && (
              <Tooltip label="Contact support" shouldWrapChildren={false}>
                <IconButton
                  size="sm"
                  variant="secondary"
                  aria-label="Open support request modal"
                  as={ReactRouterLink}
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.supportRequest,
                  })}
                >
                  <IconSupport className="self-center" />
                </IconButton>
              </Tooltip>
            )}
          </CardHeader>
          <Divider />
          <CardBody>
            <div className="flex items-center">
              <RouterHealthBadge
                routerStatus={routerStatus}
                isInitiating={routerIsInitiating}
              />
              <span className="ml-3 text-sm text-secondary">
                {routerVersion}
              </span>
            </div>
            {data.service.variant?.latestLaunch?.id && (
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <span className="text-sm text-secondary">
                  Last updated{' '}
                  <RelativeTimestamp
                    value={data.service.variant.latestLaunch.createdAt}
                  />
                </span>
                <Link
                  className="flex items-center gap-2"
                  to={launchesRouteConfig.location({
                    graphId: data.service.id,
                    graphVariant: data.service.variant.name,
                    launchId: data.service.variant.latestLaunch.id,
                  })}
                >
                  View latest launch <IconArrowRight />
                </Link>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
      <SecretsList status={routerStatus} isInitiating={routerIsInitiating} />
      <RouterConfigEditor
        canEditBuildConfig={
          data?.service?.variant?.permissions?.canManageBuildConfig
        }
        canQueryBuildConfig={
          data?.service?.variant?.permissions?.canQueryBuildConfig
        }
        defaultValue={data?.service?.variant?.routerConfig ?? ''}
        handleSaveRouterConfig={handleSaveRouterConfig}
        upsertRouterError={upsertRouterError}
        upsertResponseData={upsertResponseData}
        configSchema={routerConfigSchema}
        allSubgraphs={allSubgraphs}
      />
    </>
  );
};

const RouterHealthBadge = ({
  routerStatus,
  isInitiating,
}: {
  routerStatus: GraphQLTypes.RouterStatus | undefined;
  isInitiating: boolean;
}) => {
  const { title, icon, colorScheme } = useMemo((): {
    title: GraphQLTypes.RouterStatus | 'LOADING' | 'ERROR';
    icon?: ReactElement;
    colorScheme?: StatusBadgeProps['colorScheme'];
  } => {
    const loadingContent = {
      title: routerStatus ?? ('LOADING' as const),
      icon: <Spinner size="tiny" />,
    };
    switch (routerStatus) {
      case GraphQLTypes.RouterStatus.RUNNING:
        return {
          title: routerStatus,
          colorScheme: 'green',
        };
      case GraphQLTypes.RouterStatus.CREATING:
      case GraphQLTypes.RouterStatus.DELETING:
      case GraphQLTypes.RouterStatus.ROLLING_BACK:
      case GraphQLTypes.RouterStatus.SLEEPING:
      case GraphQLTypes.RouterStatus.UPDATING:
        return loadingContent;
      case undefined:
        return isInitiating
          ? loadingContent
          : {
              title: 'ERROR',
              colorScheme: 'red',
            };
      case GraphQLTypes.RouterStatus.DELETED:
        return {
          title: routerStatus,
          colorScheme: 'red',
        };
      default:
        return assertUnreachable(routerStatus);
    }
  }, [isInitiating, routerStatus]);

  return (
    <StatusBadge icon={icon} colorScheme={colorScheme}>
      {capitalize(title.toLowerCase())}
    </StatusBadge>
  );
};
